import { CardSchemeId } from './card_scheme_id.js';
import { Gateway } from './gateway.js';
import { allEnumValues } from './helper.js';

export function name(cardSchemeId: CardSchemeId): string {
  switch (cardSchemeId) {
    case CardSchemeId.Other:
      return 'Debit/Credit Card';
    case CardSchemeId.Visa:
      return 'Visa';
    case CardSchemeId.MasterCard:
      return 'MasterCard';
    case CardSchemeId.Maestro:
      return 'Maestro';
    case CardSchemeId.AmEx:
      return 'AmEx';
    case CardSchemeId.JCB:
      return 'JCB';
    case CardSchemeId.Diners:
      return 'Diners';
    case CardSchemeId.Discover:
      return 'Discover';
    case CardSchemeId.Voyager:
      return 'Voyager';
    case CardSchemeId.ChinaUnionPay:
      return 'China UnionPay';
    case CardSchemeId.Cash:
      return 'Cash';
    case CardSchemeId.Debit:
      return 'Debit';
    case CardSchemeId.Interac:
      return 'Interac';
    case CardSchemeId.EbtCashBenefit:
      return 'EBT Cash Benefit';
    case CardSchemeId.EbtFoodStamp:
      return 'EBT Food Stamp';
    case CardSchemeId.SquareGiftCard:
      return 'Square Gift Card';
    case CardSchemeId.SquareEftPos:
      return 'Square EFT POS';
    case CardSchemeId.LevelUpDiscount:
      return 'LevelUp Discount';
    case CardSchemeId.LevelUpGiftCard:
      return 'LevelUp Gift Card';
    case CardSchemeId.LevelUpCreditCard:
      return 'LevelUp Credit Card';
    case CardSchemeId.LovesDrinkRefills:
      return "Love's Drink Refills";
    case CardSchemeId.LovesRewardsPoints:
      return "Love's Rewards Points";
    case CardSchemeId.Atrium:
      return 'Atrium Student Card';
    case CardSchemeId.AtriumMealExchange:
      return 'Atrium Meal Exchange';
    case CardSchemeId.TCC:
    case CardSchemeId.Givex:
    case CardSchemeId.PinataStoredValue:
    case CardSchemeId.Archer:
    case CardSchemeId.PaytronixGiftCard:
    case CardSchemeId.Valutec:
    case CardSchemeId.OLOGiftCard:
    case CardSchemeId.WorldpayGiftCard:
    case CardSchemeId.FreedomPayGiftCard:
    case CardSchemeId.EigenGiftCard:
      return 'Gift Card';
    case CardSchemeId.FreedomPayStoredValue:
      return 'Stored Value';
    case CardSchemeId.FreedomPayVoucher:
      return 'Voucher';
    case CardSchemeId.Cbord:
      return 'CBORD';
    case CardSchemeId.TransactCampusCard:
      return 'Transact Campus Card';
    case CardSchemeId.ChoptDiscount:
      return 'Chopt Discount';
    case CardSchemeId.ChoptGiftCard:
      return 'Chopt Gift Card';
    case CardSchemeId.ChoptCreditCard:
      return 'Chopt Credit Card';
    case CardSchemeId.ComoPointRedemption:
      return 'Como Point Redemption';
    case CardSchemeId.ThanxReward:
      return 'Thanx Reward';
    case CardSchemeId.ThanxPointRedemption:
      return 'Thanx Point Redemption';
    case CardSchemeId.ParPayGiftCard:
      return 'Par Pay Gift Card';
    case CardSchemeId.PunchhCreditCard:
      return 'Punchh Credit Card';
    case CardSchemeId.PunchhGiftCard:
      return 'Punchh Gift Card';
    case CardSchemeId.PunchhDiscount:
      return 'Punchh Discount';
    case CardSchemeId.Quickcharge:
      return 'Quickcharge';
    case CardSchemeId.PinataCreditCard:
      return 'Pinata Credit Card';
    case CardSchemeId.PinataLoyaltyGiftCard:
      return 'Pinata Gift Card';
    case CardSchemeId.PinataDiscount:
      return 'Pinata Discount';
    case CardSchemeId.AppfrontPayment:
      return 'Appfront Payment';
    case CardSchemeId.AppfrontReward:
      return 'Appfront Reward';
    case CardSchemeId.GivexLoyaltyReward:
      return 'Givex Loyalty Reward';
    case CardSchemeId.GivexLoyaltyPayment:
      return 'Givex Loyalty Payment';
    case CardSchemeId.SmoothCommercePayment:
      return 'Smooth Commerce Payment';
    case CardSchemeId.SpendgoReward:
      return 'Spendgo Reward';
    case CardSchemeId.StarbucksStoredValue:
      return 'Starbucks Card';
    case CardSchemeId.StarbucksInAppPayment:
      return 'Starbucks Card';
    case CardSchemeId.PinataCampusCard:
      return 'Pinata Campus Card';
  }
}

/** Atrium campus and gift cards share a scheme and gateway; only the marker distinguishes them. */
export function isCampusCard(
  cardSchemeId: CardSchemeId,
  gateway?: Gateway,
  isCampusCardTransaction = false,
): boolean {
  switch (cardSchemeId) {
    case CardSchemeId.Cbord:
    case CardSchemeId.PinataCampusCard:
    case CardSchemeId.Quickcharge:
    case CardSchemeId.TransactCampusCard:
      return true;
    case CardSchemeId.Atrium:
    case CardSchemeId.AtriumMealExchange:
      return gateway === Gateway.AtriumStoredValue && isCampusCardTransaction;
    default:
      return false;
  }
}

export function isStoredValue(cardSchemeId: CardSchemeId, gateway: Gateway): boolean {
  switch (cardSchemeId) {
    // A campus-card meal exchange is a stored-value payment (a meal swipe) — guests must
    // be able to remove it from an unclosed order like any other gift-card payment.
    case CardSchemeId.Atrium:
    case CardSchemeId.AtriumMealExchange:
      return gateway === Gateway.AtriumStoredValue;
    case CardSchemeId.StarbucksStoredValue:
      return gateway === Gateway.StarbucksStoredValue;
    case CardSchemeId.AmEx:
    case CardSchemeId.Cash:
    case CardSchemeId.ChinaUnionPay:
    case CardSchemeId.ChoptDiscount:
    case CardSchemeId.Debit:
    case CardSchemeId.Diners:
    case CardSchemeId.Discover:
    case CardSchemeId.EbtCashBenefit:
    case CardSchemeId.EbtFoodStamp:
    case CardSchemeId.Interac:
    case CardSchemeId.JCB:
    case CardSchemeId.LovesDrinkRefills:
    case CardSchemeId.LovesRewardsPoints:
    case CardSchemeId.Maestro:
    case CardSchemeId.MasterCard:
    case CardSchemeId.Other:
    case CardSchemeId.PinataCreditCard:
    case CardSchemeId.PinataDiscount:
    case CardSchemeId.AppfrontPayment:
    case CardSchemeId.AppfrontReward:
    case CardSchemeId.GivexLoyaltyReward:
    case CardSchemeId.GivexLoyaltyPayment:
    case CardSchemeId.PunchhCreditCard:
    case CardSchemeId.PunchhDiscount:
    case CardSchemeId.SmoothCommercePayment:
    case CardSchemeId.SquareEftPos:
    case CardSchemeId.StarbucksInAppPayment:
    case CardSchemeId.Visa:
    case CardSchemeId.Voyager:
      return false;
    case CardSchemeId.Archer:
    case CardSchemeId.Cbord:
    // Yes, this technically is a credit card but overall LevelUp/Chopt works like
    // stored value from our perspective.
    // oxlint-disable-next-line no-fallthrough
    case CardSchemeId.ChoptCreditCard:
    case CardSchemeId.ChoptGiftCard:
    case CardSchemeId.ComoPointRedemption:
    case CardSchemeId.EigenGiftCard:
    case CardSchemeId.FreedomPayGiftCard:
    case CardSchemeId.FreedomPayStoredValue:
    case CardSchemeId.FreedomPayVoucher:
    case CardSchemeId.Givex:
    case CardSchemeId.LevelUpCreditCard:
    case CardSchemeId.LevelUpDiscount:
    case CardSchemeId.LevelUpGiftCard:
    case CardSchemeId.OLOGiftCard:
    case CardSchemeId.ParPayGiftCard:
    case CardSchemeId.PaytronixGiftCard:
    case CardSchemeId.PinataCampusCard:
    case CardSchemeId.PinataLoyaltyGiftCard:
    case CardSchemeId.PinataStoredValue:
    case CardSchemeId.PunchhGiftCard:
    case CardSchemeId.Quickcharge:
    case CardSchemeId.SpendgoReward:
    case CardSchemeId.SquareGiftCard:
    case CardSchemeId.TCC:
    case CardSchemeId.ThanxPointRedemption:
    case CardSchemeId.ThanxReward:
    case CardSchemeId.TransactCampusCard:
    case CardSchemeId.Valutec:
    case CardSchemeId.WorldpayGiftCard:
      return true;
  }
}

export function isStoredValueOrLoyalty(cardSchemeId: CardSchemeId): boolean {
  switch (cardSchemeId) {
    case CardSchemeId.AmEx:
    case CardSchemeId.Cash:
    case CardSchemeId.ChinaUnionPay:
    case CardSchemeId.Debit:
    case CardSchemeId.Diners:
    case CardSchemeId.Discover:
    case CardSchemeId.EbtCashBenefit:
    case CardSchemeId.EbtFoodStamp:
    case CardSchemeId.Interac:
    case CardSchemeId.JCB:
    case CardSchemeId.Maestro:
    case CardSchemeId.MasterCard:
    case CardSchemeId.Other:
    case CardSchemeId.PinataCreditCard:
    case CardSchemeId.SquareEftPos:
    case CardSchemeId.Visa:
    case CardSchemeId.Voyager:
      return false;
    case CardSchemeId.Archer:
    case CardSchemeId.Atrium:
    case CardSchemeId.AtriumMealExchange:
    case CardSchemeId.Cbord:
    // Yes, this technically is a credit card but overall LevelUp/Chopt works like
    // stored value from our perspective.
    // oxlint-disable-next-line no-fallthrough
    case CardSchemeId.ChoptCreditCard:
    case CardSchemeId.ChoptDiscount:
    case CardSchemeId.ChoptGiftCard:
    case CardSchemeId.ComoPointRedemption:
    case CardSchemeId.EigenGiftCard:
    case CardSchemeId.FreedomPayGiftCard:
    case CardSchemeId.FreedomPayStoredValue:
    case CardSchemeId.FreedomPayVoucher:
    case CardSchemeId.Givex:
    case CardSchemeId.LevelUpCreditCard:
    case CardSchemeId.LevelUpDiscount:
    case CardSchemeId.LevelUpGiftCard:
    case CardSchemeId.LovesDrinkRefills:
    case CardSchemeId.LovesRewardsPoints:
    case CardSchemeId.OLOGiftCard:
    case CardSchemeId.ParPayGiftCard:
    case CardSchemeId.PaytronixGiftCard:
    case CardSchemeId.PinataCampusCard:
    case CardSchemeId.PinataDiscount:
    case CardSchemeId.PinataLoyaltyGiftCard:
    case CardSchemeId.PinataStoredValue:
    case CardSchemeId.PunchhCreditCard:
    case CardSchemeId.PunchhDiscount:
    case CardSchemeId.PunchhGiftCard:
    case CardSchemeId.AppfrontPayment:
    case CardSchemeId.AppfrontReward:
    case CardSchemeId.GivexLoyaltyReward:
    case CardSchemeId.GivexLoyaltyPayment:
    case CardSchemeId.Quickcharge:
    case CardSchemeId.SmoothCommercePayment:
    case CardSchemeId.SpendgoReward:
    case CardSchemeId.SquareGiftCard:
    case CardSchemeId.StarbucksStoredValue:
    case CardSchemeId.StarbucksInAppPayment: // TODO - temp fix for the two different Starbucks types, reassess when we do their rewards for real
    case CardSchemeId.TCC:
    case CardSchemeId.ThanxPointRedemption:
    case CardSchemeId.ThanxReward:
    case CardSchemeId.TransactCampusCard:
    case CardSchemeId.Valutec:
    case CardSchemeId.WorldpayGiftCard:
      return true;
  }
}

export function getStoredValueAndLoyaltyIds(): CardSchemeId[] {
  return allEnumValues<CardSchemeId>(CardSchemeId).filter(isStoredValueOrLoyalty);
}

export function isLoyalty(cardSchemeId: CardSchemeId, gateway: Gateway): boolean {
  switch (cardSchemeId) {
    case CardSchemeId.Atrium:
      return gateway === Gateway.AtriumLoyalty;
    case CardSchemeId.StarbucksStoredValue:
      return gateway === Gateway.Starbucks;
    case CardSchemeId.AtriumMealExchange:
    case CardSchemeId.ChoptDiscount:
    case CardSchemeId.ComoPointRedemption:
    case CardSchemeId.LevelUpDiscount:
    case CardSchemeId.LovesDrinkRefills:
    case CardSchemeId.LovesRewardsPoints:
    case CardSchemeId.PinataCreditCard:
    case CardSchemeId.PinataDiscount:
    case CardSchemeId.PinataLoyaltyGiftCard:
    case CardSchemeId.PunchhCreditCard:
    case CardSchemeId.PunchhDiscount:
    case CardSchemeId.PunchhGiftCard:
    case CardSchemeId.AppfrontPayment:
    case CardSchemeId.AppfrontReward:
    case CardSchemeId.GivexLoyaltyReward:
    case CardSchemeId.GivexLoyaltyPayment:
    case CardSchemeId.SmoothCommercePayment:
    case CardSchemeId.SpendgoReward:
    case CardSchemeId.StarbucksInAppPayment:
    case CardSchemeId.ThanxPointRedemption:
    case CardSchemeId.ThanxReward:
      return true;
    case CardSchemeId.AmEx:
    case CardSchemeId.Archer:
    case CardSchemeId.Cash:
    case CardSchemeId.Cbord:
    case CardSchemeId.ChinaUnionPay:
    case CardSchemeId.ChoptCreditCard:
    case CardSchemeId.ChoptGiftCard:
    case CardSchemeId.Debit:
    case CardSchemeId.Diners:
    case CardSchemeId.Discover:
    case CardSchemeId.EbtCashBenefit:
    case CardSchemeId.EbtFoodStamp:
    case CardSchemeId.EigenGiftCard:
    case CardSchemeId.FreedomPayGiftCard:
    case CardSchemeId.FreedomPayStoredValue:
    case CardSchemeId.FreedomPayVoucher:
    case CardSchemeId.Givex:
    case CardSchemeId.Interac:
    case CardSchemeId.JCB:
    case CardSchemeId.LevelUpCreditCard:
    case CardSchemeId.LevelUpGiftCard:
    case CardSchemeId.Maestro:
    case CardSchemeId.MasterCard:
    case CardSchemeId.OLOGiftCard:
    case CardSchemeId.Other:
    case CardSchemeId.ParPayGiftCard:
    case CardSchemeId.PaytronixGiftCard:
    case CardSchemeId.PinataCampusCard:
    case CardSchemeId.PinataStoredValue:
    case CardSchemeId.Quickcharge:
    case CardSchemeId.SquareEftPos:
    case CardSchemeId.SquareGiftCard:
    case CardSchemeId.TCC:
    case CardSchemeId.TransactCampusCard:
    case CardSchemeId.Valutec:
    case CardSchemeId.Visa:
    case CardSchemeId.Voyager:
    case CardSchemeId.WorldpayGiftCard:
      return false;
  }
}

export type CardSchemeIdString =
  | 'other'
  | 'visa'
  | 'master-card'
  | 'maestro'
  | 'amex'
  | 'jcb'
  | 'diners'
  | 'discover'
  | 'voyager'
  | 'cbord'
  | 'quickcharge'
  | 'china-union-pay'
  | 'cash'
  | 'debit'
  | 'interac'
  | 'ebt-cash-benefit'
  | 'ebt-food-stamp'
  | 'square-gift-card'
  | 'square-eft-pos'
  | 'level-up-discount'
  | 'level-up-gift-card'
  | 'level-up-credit-card'
  | 'loves-drink-refills'
  | 'loves-rewards-points'
  | 'atrium'
  | 'atrium-meal-exchange'
  | 'tcc'
  | 'givex'
  | 'pinata-stored-value'
  | 'archer'
  | 'paytronix-gift-card'
  | 'valutec'
  | 'olo-gift-card'
  | 'worldpay-gift-card'
  | 'freedom-pay-gift-card'
  | 'freedom-pay-stored-value'
  | 'freedom-pay-voucher'
  | 'eigen-gift-card'
  | 'chopt-discount'
  | 'chopt-gift-card'
  | 'chopt-credit-card'
  | 'como-point-redemption'
  | 'thanx-reward'
  | 'thanx-point-redemption'
  | 'appfront-payment'
  | 'appfront-reward'
  | 'givex-loyalty-reward'
  | 'givex-loyalty-payment'
  | 'smooth-commerce-payment'
  | 'spendgo-reward'
  | 'starbucks-stored-value'
  | 'starbucks-reward'
  | 'par-pay-gift-card'
  | 'punchh-credit-card'
  | 'punchh-gift-card'
  | 'punchh-discount'
  | 'pinata-credit-card'
  | 'pinata-gift-card'
  | 'pinata-discount'
  | 'pinata-campus-card'
  | 'transact-campus-card';

export function stringFromEnum(cardSchemeId: CardSchemeId): CardSchemeIdString {
  switch (cardSchemeId) {
    case CardSchemeId.Other:
      return 'other';
    case CardSchemeId.Visa:
      return 'visa';
    case CardSchemeId.MasterCard:
      return 'master-card';
    case CardSchemeId.Maestro:
      return 'maestro';
    case CardSchemeId.AmEx:
      return 'amex';
    case CardSchemeId.JCB:
      return 'jcb';
    case CardSchemeId.Diners:
      return 'diners';
    case CardSchemeId.Discover:
      return 'discover';
    case CardSchemeId.Voyager:
      return 'voyager';
    case CardSchemeId.Cbord:
      return 'cbord';
    case CardSchemeId.Quickcharge:
      return 'quickcharge';
    case CardSchemeId.ChinaUnionPay:
      return 'china-union-pay';
    case CardSchemeId.Cash:
      return 'cash';
    case CardSchemeId.Debit:
      return 'debit';
    case CardSchemeId.Interac:
      return 'interac';
    case CardSchemeId.EbtCashBenefit:
      return 'ebt-cash-benefit';
    case CardSchemeId.EbtFoodStamp:
      return 'ebt-food-stamp';
    case CardSchemeId.SquareGiftCard:
      return 'square-gift-card';
    case CardSchemeId.SquareEftPos:
      return 'square-eft-pos';
    case CardSchemeId.LevelUpDiscount:
      return 'level-up-discount';
    case CardSchemeId.LevelUpGiftCard:
      return 'level-up-gift-card';
    case CardSchemeId.LevelUpCreditCard:
      return 'level-up-credit-card';
    case CardSchemeId.LovesDrinkRefills:
      return 'loves-drink-refills';
    case CardSchemeId.LovesRewardsPoints:
      return 'loves-rewards-points';
    case CardSchemeId.Atrium:
      return 'atrium';
    case CardSchemeId.AtriumMealExchange:
      return 'atrium-meal-exchange';
    case CardSchemeId.TCC:
      return 'tcc';
    case CardSchemeId.Givex:
      return 'givex';
    case CardSchemeId.PinataStoredValue:
      return 'pinata-stored-value';
    case CardSchemeId.Archer:
      return 'archer';
    case CardSchemeId.PaytronixGiftCard:
      return 'paytronix-gift-card';
    case CardSchemeId.Valutec:
      return 'valutec';
    case CardSchemeId.OLOGiftCard:
      return 'olo-gift-card';
    case CardSchemeId.WorldpayGiftCard:
      return 'worldpay-gift-card';
    case CardSchemeId.FreedomPayGiftCard:
      return 'freedom-pay-gift-card';
    case CardSchemeId.FreedomPayStoredValue:
      return 'freedom-pay-stored-value';
    case CardSchemeId.FreedomPayVoucher:
      return 'freedom-pay-voucher';
    case CardSchemeId.EigenGiftCard:
      return 'eigen-gift-card';
    case CardSchemeId.ChoptDiscount:
      return 'chopt-discount';
    case CardSchemeId.ChoptGiftCard:
      return 'chopt-gift-card';
    case CardSchemeId.ChoptCreditCard:
      return 'chopt-credit-card';
    case CardSchemeId.ComoPointRedemption:
      return 'como-point-redemption';
    case CardSchemeId.ThanxReward:
      return 'thanx-reward';
    case CardSchemeId.ThanxPointRedemption:
      return 'thanx-point-redemption';
    case CardSchemeId.ParPayGiftCard:
      return 'par-pay-gift-card';
    case CardSchemeId.PunchhCreditCard:
      return 'punchh-credit-card';
    case CardSchemeId.PunchhGiftCard:
      return 'punchh-gift-card';
    case CardSchemeId.PunchhDiscount:
      return 'punchh-discount';
    case CardSchemeId.PinataCreditCard:
      return 'pinata-credit-card';
    case CardSchemeId.PinataLoyaltyGiftCard:
      return 'pinata-gift-card';
    case CardSchemeId.PinataDiscount:
      return 'pinata-discount';
    case CardSchemeId.AppfrontPayment:
      return 'appfront-payment';
    case CardSchemeId.AppfrontReward:
      return 'appfront-reward';
    case CardSchemeId.GivexLoyaltyReward:
      return 'givex-loyalty-reward';
    case CardSchemeId.GivexLoyaltyPayment:
      return 'givex-loyalty-payment';
    case CardSchemeId.SmoothCommercePayment:
      return 'smooth-commerce-payment';
    case CardSchemeId.SpendgoReward:
      return 'spendgo-reward';
    case CardSchemeId.StarbucksStoredValue:
      return 'starbucks-stored-value';
    case CardSchemeId.StarbucksInAppPayment:
      return 'starbucks-reward';
    case CardSchemeId.PinataCampusCard:
      return 'pinata-campus-card';
    case CardSchemeId.TransactCampusCard:
      return 'transact-campus-card';
  }
}

export function isEbt(cardSchemeId: CardSchemeId): boolean {
  switch (cardSchemeId) {
    case CardSchemeId.EbtCashBenefit:
    case CardSchemeId.EbtFoodStamp:
      return true;
    case CardSchemeId.AmEx:
    case CardSchemeId.Archer:
    case CardSchemeId.Atrium:
    case CardSchemeId.AtriumMealExchange:
    case CardSchemeId.Cash:
    case CardSchemeId.Cbord:
    case CardSchemeId.ChinaUnionPay:
    case CardSchemeId.ChoptCreditCard:
    case CardSchemeId.ChoptDiscount:
    case CardSchemeId.ChoptGiftCard:
    case CardSchemeId.ComoPointRedemption:
    case CardSchemeId.Debit:
    case CardSchemeId.Diners:
    case CardSchemeId.Discover:
    case CardSchemeId.EigenGiftCard:
    case CardSchemeId.FreedomPayGiftCard:
    case CardSchemeId.FreedomPayStoredValue:
    case CardSchemeId.FreedomPayVoucher:
    case CardSchemeId.Givex:
    case CardSchemeId.Interac:
    case CardSchemeId.JCB:
    case CardSchemeId.LevelUpCreditCard:
    case CardSchemeId.LevelUpDiscount:
    case CardSchemeId.LevelUpGiftCard:
    case CardSchemeId.LovesDrinkRefills:
    case CardSchemeId.LovesRewardsPoints:
    case CardSchemeId.Maestro:
    case CardSchemeId.MasterCard:
    case CardSchemeId.OLOGiftCard:
    case CardSchemeId.Other:
    case CardSchemeId.PaytronixGiftCard:
    case CardSchemeId.PinataStoredValue:
    case CardSchemeId.PunchhGiftCard:
    case CardSchemeId.PunchhCreditCard:
    case CardSchemeId.PunchhDiscount:
    case CardSchemeId.Quickcharge:
    case CardSchemeId.SquareEftPos:
    case CardSchemeId.SquareGiftCard:
    case CardSchemeId.TCC:
    case CardSchemeId.Valutec:
    case CardSchemeId.Visa:
    case CardSchemeId.Voyager:
    case CardSchemeId.WorldpayGiftCard:
    case CardSchemeId.ThanxReward:
    case CardSchemeId.ThanxPointRedemption:
    case CardSchemeId.ParPayGiftCard:
    case CardSchemeId.PinataCreditCard:
    case CardSchemeId.PinataLoyaltyGiftCard:
    case CardSchemeId.PinataDiscount:
    case CardSchemeId.AppfrontPayment:
    case CardSchemeId.AppfrontReward:
    case CardSchemeId.GivexLoyaltyReward:
    case CardSchemeId.GivexLoyaltyPayment:
    case CardSchemeId.SmoothCommercePayment:
    case CardSchemeId.SpendgoReward:
    case CardSchemeId.StarbucksStoredValue:
    case CardSchemeId.StarbucksInAppPayment: // TODO - temp fix for the two different Starbucks types, reassess when we do their rewards for real
    case CardSchemeId.PinataCampusCard:
    case CardSchemeId.TransactCampusCard:
      return false;
  }
}
